/**
 * Éditeur de champs extraits par l'IA.
 *
 * - Affiche toujours Nom Complet et Date (champs obligatoires)
 * - Permet la correction inline au clic sur le crayon
 * - Crée automatiquement un champ s'il n'existe pas encore (via POST)
 * - Modulaire : pour ajouter un champ, l'ajouter dans FIELD_LABELS et MANDATORY_FIELDS
 */
import { useState } from 'react';
import { Pencil, Check, X, RefreshCw } from 'lucide-react';
import { documentsApi } from '../api';
import type { ExtractedData } from '../types';

// ── Labels français ────────────────────────────────────────────
// Pour ajouter un champ : l'ajouter ici + dans MANDATORY_FIELDS si nécessaire
const FIELD_LABELS: Record<string, string> = {
  nom_complet: 'Nom Complet',
  date: 'Date',
};

export function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// Champs toujours affichés même si non extraits
const MANDATORY_FIELDS = ['nom_complet', 'date'];

interface Props {
  docId: string;
  extractedData: ExtractedData[];
  onUpdated: () => void;
}

export function ExtractedFieldsEditor({ docId, extractedData, onUpdated }: Props) {
  const [editingKey, setEditingKey] = useState<string | null>(null); // field_name ou field_id
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Fusion : champs extraits + champs obligatoires manquants
  const extractedMap = new Map(extractedData.map(f => [f.field_name, f]));
  const allFields: Array<{ key: string; existing?: ExtractedData }> = [
    // Champs obligatoires en premier
    ...MANDATORY_FIELDS.map(key => ({ key, existing: extractedMap.get(key) })),
    // Puis tous les autres champs extraits non obligatoires (champs additionnels futurs)
    ...extractedData
      .filter(f => !MANDATORY_FIELDS.includes(f.field_name))
      .map(f => ({ key: f.field_name, existing: f })),
  ];

  const startEdit = (key: string, currentValue?: string) => {
    setEditingKey(key);
    setEditValue(currentValue ?? '');
  };

  const cancelEdit = () => { setEditingKey(null); setEditValue(''); };

  const saveField = async (key: string) => {
    if (!editValue.trim()) { cancelEdit(); return; }
    setSaving(true);
    try {
      const existing = extractedMap.get(key);
      if (existing) {
        await documentsApi.correctField(docId, existing.id, editValue.trim());
      } else {
        await documentsApi.addField(docId, key, editValue.trim());
      }
      onUpdated();
    } finally {
      setSaving(false);
      cancelEdit();
    }
  };

  return (
    <div className="space-y-0">
      {allFields.map(({ key, existing }, idx) => {
        const isEditing = editingKey === key;
        const isMandatory = MANDATORY_FIELDS.includes(key);
        const isCorrected = existing?.is_manually_corrected ?? false;
        const value = existing?.field_value;
        const isEmpty = !value;
        const isLast = idx === allFields.length - 1;

        return (
          <div
            key={key}
            className={`group flex items-center gap-2 py-2.5 px-1 ${!isLast ? 'border-b border-gray-100' : ''}`}
          >
            {/* Label */}
            <span className={`text-sm shrink-0 w-36 ${isMandatory && isEmpty ? 'text-orange-500 font-medium' : 'text-gray-500'}`}>
              {fieldLabel(key)}
              {isMandatory && <span className="text-orange-400 ml-0.5">*</span>}
            </span>

            {/* Valeur / Champ d'édition */}
            {isEditing ? (
              <div className="flex items-center gap-1.5 flex-1">
                <input
                  autoFocus
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveField(key);
                    if (e.key === 'Escape') cancelEdit();
                  }}
                  placeholder={fieldLabel(key)}
                  className="flex-1 border border-teal-300 rounded-lg px-2.5 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                />
                <button
                  onClick={() => saveField(key)}
                  disabled={saving}
                  className="p-1.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white rounded-lg"
                >
                  {saving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                </button>
                <button onClick={cancelEdit} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <X className="w-3 h-3 text-gray-400" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1 flex-1 justify-end">
                <span
                  className={`text-sm font-semibold text-right truncate max-w-[55%] ${
                    isEmpty
                      ? 'text-gray-300 font-normal italic'
                      : isCorrected
                      ? 'text-teal-700'
                      : 'text-gray-900'
                  }`}
                  title={value ?? ''}
                >
                  {isEmpty ? 'Non renseigné' : value}
                </span>
                <button
                  onClick={() => startEdit(key, value)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded-lg transition"
                  title="Modifier"
                >
                  <Pencil className="w-3 h-3 text-gray-400 hover:text-teal-600" />
                </button>
              </div>
            )}
          </div>
        );
      })}

      {allFields.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-4 italic">Aucune information extraite</p>
      )}

      {MANDATORY_FIELDS.some(k => !extractedMap.get(k)?.field_value) && (
        <p className="text-xs text-orange-500 mt-2">
          * Champs obligatoires — cliquez sur le crayon pour les renseigner.
        </p>
      )}
    </div>
  );
}
